import React from "react";

import { useAppSelector } from "../../../app/store";

import { EntityType, RelatedAs} from "../../../api/stakeholder_relationships";
import { getStakeholderById, getStakeholderRelationshipsByEntity} from "../../stakeholder/logic/stakeholderSlice";
import StakeholderItem from "../../stakeholder/components/StakeholderItem";

const BORROWER_RELATIONSHIPS = [
  RelatedAs.PRIMARY_BORROWER,
  RelatedAs.CO_BORROWER,
];

export interface AccountBorrowersProps {
  accountNumber: string;
  onClick?: (stakeholderId: string) => void;
}

const AccountBorrowers: React.FC<AccountBorrowersProps> = ({
  accountNumber,
  onClick,
}) => {
  const relationships = useAppSelector((state) =>
    getStakeholderRelationshipsByEntity(
      state,
      EntityType.LOAN_ACCOUNT,
      accountNumber,
      BORROWER_RELATIONSHIPS,
    ),
  );

  const primaryBorrowerRelationship = relationships.find(
    (relationship) =>
      relationship.related_as === RelatedAs.PRIMARY_BORROWER,
  );

  const coBorrowerRelationships = relationships.filter(
    (relationship) =>
      relationship.related_as === RelatedAs.CO_BORROWER,
  );

  const primaryBorrower = useAppSelector((state) =>
    primaryBorrowerRelationship
      ? getStakeholderById(
          state,
          primaryBorrowerRelationship.stakeholder_id,
        )
      : undefined,
  );

  const coBorrowers = useAppSelector((state) =>
    coBorrowerRelationships
      .map((relationship) =>
        getStakeholderById(
          state,
          relationship.stakeholder_id,
        ),
      )
      .filter(Boolean),
  );

  const borrowerCount =
    (primaryBorrower ? 1 : 0) + coBorrowers.length;

  return (
    <div className="px-4">
      <div className="flex w-full flex-col gap-3 rounded-[8px] bg-[#F3F4F6] p-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate font-sans text-[12px] font-medium leading-none text-[#737373]">
            Borrowers
          </span>

          <span
            className="
              inline-flex
              min-w-[20px]
              items-center
              justify-center
              rounded-[8px]
              border
              border-transparent
              bg-[#EBEEFE]
              px-2
              py-[2px]
              font-sans
              text-[12px]
              font-semibold
              leading-4
              text-[#3551F3]
            "
          >
            {borrowerCount}
          </span>
        </div>

        <div className="flex w-full flex-col gap-2">
          {borrowerCount > 0 ? (
            <>
              {primaryBorrower && (
                <StakeholderItem
                  name={primaryBorrower.name}
                  relatedAs={RelatedAs.PRIMARY_BORROWER}
                  onClick={() =>
                    onClick?.(primaryBorrower.stakeholder_id)
                  }
                  className="
                    w-full
                    border-transparent
                  "
                />
              )}

              {coBorrowers.map((coBorrower) => {
                if (!coBorrower) {
                  return null;
                }

                return (
                  <StakeholderItem
                    key={coBorrower.stakeholder_id}
                    name={coBorrower.name}
                    relatedAs={RelatedAs.CO_BORROWER}
                    onClick={() =>
                      onClick?.(coBorrower.stakeholder_id)
                    }
                    className="
                      w-full
                      border-transparent
                    "
                  />
                );
              })}
            </>
          ) : (
            <span className="text-[12px] font-normal leading-4 text-[#737373]">
              No borrowers found
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountBorrowers;